import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, CheckCircle, XCircle, Clock, Crown } from 'lucide-react';

const leaderboardData = [
  { name: 'Alex J.', picks: [
    { logo: 'https://resources.premierleague.com/premierleague25/badges/3.svg', status: 'won' },
    { logo: 'https://resources.premierleague.com/premierleague25/badges/8.svg', status: 'won' },
    { logo: 'https://resources.premierleague.com/premierleague25/badges/14.svg', status: 'pending' },
  ]},
  { name: 'Sam W.', picks: [
    { logo: 'https://resources.premierleague.com/premierleague25/badges/43.svg', status: 'won' },
    { logo: 'https://resources.premierleague.com/premierleague25/badges/3.svg', status: 'won' },
    { logo: 'https://resources.premierleague.com/premierleague25/badges/6.svg', status: 'pending' },
  ]},
  { name: 'Jordan T.', isCurrentUser: true, picks: [
    { logo: 'https://resources.premierleague.com/premierleague25/badges/14.svg', status: 'won' },
    { logo: 'https://resources.premierleague.com/premierleague25/badges/1.svg', status: 'won' },
    { logo: 'https://resources.premierleague.com/premierleague25/badges/8.svg', status: 'pending' },
  ]},
  { name: 'Riley B.', isEliminated: true, picks: [
    { logo: 'https://resources.premierleague.com/premierleague25/badges/6.svg', status: 'won' },
    { logo: 'https://resources.premierleague.com/premierleague25/badges/8.svg', status: 'lost' },
  ]},
];

export const LeaderboardDemo: React.FC = () => {
  const [visibleRows, setVisibleRows] = useState(0);
  const [showCrown, setShowCrown] = useState(false);
  const [highlightUser, setHighlightUser] = useState(false);

  useEffect(() => {
    const sequence = [
      () => { setVisibleRows(0); setShowCrown(false); setHighlightUser(false); },
      () => setVisibleRows(1),
      () => setVisibleRows(2),
      () => setVisibleRows(3),
      () => setVisibleRows(4),
      () => setShowCrown(true),
      () => setHighlightUser(true),
    ];
    const timings = [0, 400, 700, 1000, 1300, 1800, 2200];
    const runSequence = () => sequence.forEach((action, i) => setTimeout(action, timings[i]));
    runSequence();
    const interval = setInterval(runSequence, 7000);
    return () => clearInterval(interval);
  }, []);

  const getIcon = (status: string) => {
    if (status === 'won') return <CheckCircle className="h-2.5 w-2.5 text-emerald-500" />;
    if (status === 'lost') return <XCircle className="h-2.5 w-2.5 text-red-500" />;
    return <Clock className="h-2.5 w-2.5 text-blue-500" />;
  };

  return (
    <div className="flex flex-col h-full pt-6 pb-4 px-1">
      <div className="bg-white/80 rounded-xl shadow-lg border border-gray-200/50 overflow-hidden flex-1">
        <div className="bg-gradient-to-r from-emerald-600 to-green-600 p-2">
          <div className="flex items-center space-x-2">
            <div className="bg-white/20 p-1 rounded-lg"><Target className="h-3 w-3 text-white" /></div>
            <div><h3 className="text-xs font-bold text-white">Active Players</h3></div>
          </div>
        </div>
        <div className="divide-y divide-gray-200/50">
          <AnimatePresence>
            {leaderboardData.slice(0, visibleRows).map((user, index) => (
              <motion.div key={user.name} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                className={`flex p-2 ${user.isCurrentUser && highlightUser ? 'bg-blue-100/80 border-l-2 border-blue-500' : user.isEliminated ? 'bg-red-50/50' : ''}`}>
                <div className="flex-shrink-0 w-20 flex flex-col">
                  <div className="flex items-center gap-1">
                    {index === 0 && showCrown && <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}><Crown className="h-3 w-3 text-yellow-500 fill-yellow-500" /></motion.div>}
                    <span className={`font-semibold text-[10px] ${user.isCurrentUser ? 'text-blue-900' : user.isEliminated ? 'text-gray-500' : 'text-gray-900'}`}>{user.name}</span>
                  </div>
                  {user.isCurrentUser && highlightUser && <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="bg-blue-500/20 text-blue-600 text-[7px] px-1.5 py-0.5 rounded-full font-medium w-fit mt-0.5">You</motion.span>}
                  {user.isEliminated && <span className="text-[7px] text-red-500 font-medium">Eliminated</span>}
                </div>
                <div className="flex-1 flex justify-end gap-1">
                  {user.picks.map((pick, i) => (
                    <div key={i} className={`flex flex-col items-center p-1 rounded-lg border min-w-8 ${user.isEliminated && i === user.picks.length - 1 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                      <img src={pick.logo} alt="" className="w-5 h-5 object-contain" />
                      {getIcon(pick.status)}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
      <div className="mt-auto pt-2 text-center"><span className="text-white/40 text-[10px] uppercase tracking-wider">Live Rankings</span></div>
    </div>
  );
};
